import { authFetch, fetchJSON } from '@/utils/http'
import { API_ROUTE } from '../backend/config'
import {
  CompanyPublicInfo,
  ListMembersResponse,
  OrgDailyStats,
  PendingMemberInvite,
  PlanInfo,
} from './types'

interface FetchPlanResponse {
  plan: PlanInfo
  members: ListMembersResponse
  pendingInvites: PendingMemberInvite[]
}

export const fetchPlan = async (): Promise<FetchPlanResponse> => {
  return await fetchJSON(`${API_ROUTE}/organizations/plan`)
}

export const fetchStats = async (
  interval: number,
): Promise<OrgDailyStats[]> => {
  return await fetchJSON(
    `${API_ROUTE}/organizations/stats?interval=${interval}`,
  )
}

export const inviteMember = async (email: string) => {
  return await fetchJSON(`${API_ROUTE}/organizations/members`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export const removeMemberInvite = async (email: string) => {
  return await fetchJSON(`${API_ROUTE}/organizations/members`, {
    method: 'DELETE',
    body: JSON.stringify({ email }),
  })
}

type AcceptInviteParams = {
  inviteToken: string
  password: string
  name: string
  surname: string
  jobTitle: string
  department: string
  email?: string
}

export const acceptInvite = async (params: AcceptInviteParams) => {
  return await fetchJSON(`${API_ROUTE}/organizations/accept-invite`, {
    method: 'POST',
    body: JSON.stringify({
      ...params,

      userAgent: navigator.userAgent,
    }),
  })
}

export type UpdateMemberParams = {
  active?: boolean
  role?: 'org-manager' | 'reader'
}

export const updateMember = async (
  memberId: string,
  params: UpdateMemberParams,
) => {
  return await fetchJSON(`${API_ROUTE}/organizations/members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  })
}

export const updatePlan = async (values: {
  hasRAGEnabled: boolean
}): Promise<Pick<FetchPlanResponse, 'plan'>> => {
  return await fetchJSON(`${API_ROUTE}/organizations/plan`, {
    method: 'PATCH',
    body: JSON.stringify(values),
  })
}

export const regenerateInviteLink = async (
  organizationId: string,
): Promise<{ inviteLink: string; inviteLinkActivatedAt: string }> => {
  return await fetchJSON(
    `${API_ROUTE}/organizations/${organizationId}/regenerate-invite-link`,
    {
      method: 'POST',
    },
  )
}

export const autocompleteCompanyInfoFromFiles = async (
  files: File[],
): Promise<CompanyPublicInfo> => {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })

  const response = await authFetch(
    `${API_ROUTE}/organizations/autocomplete-info`,
    {
      method: 'POST',
      body: formData,
    },
  )

  return await response.json()
}

export const fetchPublicCompanyInfo = async (): Promise<CompanyPublicInfo> => {
  return await fetchJSON(`${API_ROUTE}/organizations/info`)
}

export const updateCompanyInfo = async (
  data: CompanyPublicInfo,
): Promise<void> => {
  return await fetchJSON(`${API_ROUTE}/organizations/info`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export interface LoginEvent {
  id: number
  timestamp: Date
  userId: string
  email?: string
  name?: string
  surname?: string
  key: string
}

export interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

export interface UserLoginsResponse {
  data: LoginEvent[]
  pagination: PaginationInfo
}

export interface UserLoginsFilters {
  userEmail?: string
  startDate?: string
  endDate?: string
}

export const fetchOrgUserLogins = async (
  organizationId: string,
  limit: number = 100,
  page: number = 1,
  includeAllEvents: boolean = false,
  filters: UserLoginsFilters = {},
): Promise<UserLoginsResponse> => {
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    includeAllEvents: String(includeAllEvents),
  })

  if (filters.userEmail) params.set('userEmail', filters.userEmail)
  if (filters.startDate) params.set('startDate', filters.startDate)
  if (filters.endDate) params.set('endDate', filters.endDate)

  return await fetchJSON(
    `${API_ROUTE}/organizations/${organizationId}/user-logins?${params.toString()}`,
  )
}

export interface Organization {
  id: string
  name: string
  taxId?: string
  createdAt: string
  updatedAt: string
}

export const fetchOrganizations = async (): Promise<Organization[]> => {
  return (await fetchJSON(`${API_ROUTE}/organizations?limit=10000`)).docs
}
